import { ModulesState } from "fabrix/redux/module-slice";
import { useSelector } from "react-redux";
import { AppState } from "../redux/store";
import { useEffect, useState } from "react";
import { ModuleInfo } from "../types";
import { translate } from "../common";
import { matchPath, useLocation } from "react-router";
import { useUserPermissions } from "./use-current-user";
import { set } from "date-fns";

/**
 * Returns a sorted list of modules filtered by optional type and localized labels.
 * @param type - Optional module type used to filter the module list.
 * @returns Array of module descriptors matching the criteria.
 */
export function useModules(type?: string) {

    const _moduleState = useSelector<AppState>(state => state.modules) as ModulesState;
    const [_modules, setModules] = useState<ModuleInfo[]>([])

    useEffect(() => {
        let modules = new Array<ModuleInfo>();
        let filtered = type ? _moduleState.modules.filter(m => m.type === type) : _moduleState.modules;
        for (let index = 0; index < filtered.length; index++) {
            const module = { ...filtered[index] };
            module.label = translate(module.label);
            modules.push(module)
        }
        modules.sort((a, b) => a.label.localeCompare(b.label));
        setModules(modules)
    }, [_moduleState.modules])

    return _modules;
}

export interface ModulePage {
    page: ModuleInfo;
    module: ModuleInfo;
}


/**
 * Resolves module and page metadata for the current location.
 * @returns Function that accepts a module type and optional path, returning the matching page info.
 */
export function useModulePage() {

    //const type = "dynamic"
    const _moduleState = useSelector<AppState>(state => state.modules) as ModulesState;
    let location = useLocation();
    const [_modules, setModules] = useState<ModuleInfo[]>([])

    useEffect(() => {
        let modules = new Array<ModuleInfo>();
        let items = [..._moduleState.modules]
        for (let index = 0; index < items.length; index++) {
            const module = { ...items[index] };
            module.label = translate(module.label);
            modules.push(module)
        }
        modules.sort((a, b) => a.label.localeCompare(b.label));
        setModules(modules)
    }, [_moduleState.modules])



    /**
     * Finds the page definition for the supplied module type and pathname.
     * @param type - Module type to search within (for example, "dynamic").
     * @param pathName - Optional pathname override; defaults to current location.
     * @returns ModulePage metadata when found, otherwise undefined.
     */
    const getPage = (type: string, pathName?: string) => {

        let filtered = type ? _moduleState.modules.filter(m => m.type === type) : _moduleState.modules;
        filtered.push({
            path: "/", label: translate("CORE"), type: "system",
            name: "fbx-core",
            id: "5ed959da-e436-440d-ad6e-48cef755d4bc",
            children: [{ path: "/", label: translate("HOME"), name: "home", id: "5ac2ed64-cda1-4c75-8bb8-2b837a73bfb6", type: "system" }]
        })

        pathName = pathName ?? location.pathname;

        for (let i = 0; i < filtered.length; i++) {
            const module = filtered[i];
            if (module.children) {
                for (let j = 0; j < module.children.length; j++) {
                    const page = module.children[j];
                    let match = matchPath({ path: page.path, end: true }, pathName);
                    if (match) {
                        let pageInfo: ModulePage = {
                            page: page,
                            module: module
                        };
                        return pageInfo;
                    }

                }
            }

        }
        return undefined;

    }

    return getPage;

}


/**
 * Evaluates whether the user has edit rights for the current dynamic page.
 * @returns Boolean indicating if the page should be treated as read-only.
 */
export function usePageRights() {
    const permissions = useUserPermissions();
    const location = useLocation();
    const modulePage = useModulePage();
    const [_readOnly, setReadOnly] = useState<boolean>(false);

    useEffect(() => {

        let pageInfo = modulePage("dynamic");
        if (pageInfo !== undefined  && pageInfo.page?.id) {

            let tokenPermission = permissions[pageInfo.module?.id ?? ""];
            if (tokenPermission) {
                 let edit = tokenPermission.edit?.pages?.find(p => p === pageInfo?.page?.id);
                setReadOnly(edit === undefined);
            } else {
                setReadOnly(true);
            }
        } else {
            setReadOnly(true);
        }

    }, [permissions, location]);


    return _readOnly;
}